import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso900114001DebouncedValue, useIso900114001LatestRequest } from "../../../components/shared/useIso900114001FilteredRequest";
import type { CalidadControlModificacion } from "../../../types/control-modificaciones";
import { getCalidadPlanificacionDeCambios } from "../api/planificacion-de-cambios";
import type { CalidadPlanificacionDeCambio, CalidadPlanificacionDeCambiosFilters } from "../types/planificacion-de-cambios";

type Meta = { total: number; control_modificaciones: number; estados: string[] };
const emptyMeta: Meta = { total: 0, control_modificaciones: 0, estados: [] };

export function useCalidadPlanificacionDeCambios(filters: CalidadPlanificacionDeCambiosFilters, enabled = true) {
  const [rows, setRows] = useState<CalidadPlanificacionDeCambio[]>([]);
  const [controlModificaciones, setControlModificaciones] = useState<CalidadControlModificacion[]>([]);
  const [meta, setMeta] = useState<Meta>(emptyMeta);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso900114001DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso900114001LatestRequest();
  const requestFilters = useMemo<CalidadPlanificacionDeCambiosFilters>(() => ({ ...filters, search: debouncedSearch }), [debouncedSearch, filters.estado, filters.fecha_desde, filters.fecha_hasta]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const reload = useCallback(async (force = true) => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getCalidadPlanificacionDeCambios(requestFilters, signal), force);
      if (!response) return;
      setRows(response.data);
      setControlModificaciones(response.control_modificaciones);
      setMeta(response.meta);
    } catch {
      setError("No se pudo cargar la planificación de cambios.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [enabled, hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void reload(false); }, [reload]);

  return { rows, controlModificaciones, meta, loading, error, reload };
}
